import type { ReactElement } from 'react';
import type { LayoutChangeEvent, StyleProp, ViewStyle } from 'react-native';

import { ToggleButton, ToggleButtonProps } from './toggle-button';

export type ToggleButtonGroupChild<T> =
  | ReactElement<ToggleButtonProps<T>, any>
  | ReactElement;

type ToggleButtonGroupChildren<T> =
  | ToggleButtonGroupChild<T>
  | ToggleButtonGroupChild<T>[];

export interface ToggleButtonGroupProps<T> {
  children: ToggleButtonGroupChildren<T>;
  multiple?: boolean;
  style?: StyleProp<ViewStyle>;
  value?: T[];
  onChange?: (value?: T[]) => void;
  onLayout?: ((event: LayoutChangeEvent) => void) | undefined;
}

type CloneFunction<T> = (
  child: ToggleButtonGroupChild<T>
) => ToggleButtonGroupChild<T>;

export function renderGroup<T>(
  children: ToggleButtonGroupChildren<T>,
  clone: CloneFunction<T>
): ToggleButtonGroupChildren<T> {
  if (Array.isArray(children)) {
    return children.map((child) =>
      renderGroup(child, clone)
    ) as ToggleButtonGroupChildren<T>;
  } else {
    return renderChild(children, clone);
  }
}

function renderChild<T>(
  child: ToggleButtonGroupChild<T>,
  clone: CloneFunction<T>
): ToggleButtonGroupChild<T> {
  if (child?.props?.children) {
    return renderChildPropsChildren(child, clone);
  }
  if (child.type.name === ToggleButton.name) {
    return clone(child);
  }
  return child;
}

function renderChildPropsChildren<T>(
  child: ToggleButtonGroupChild<T>,
  clone: CloneFunction<T>
): ToggleButtonGroupChild<T> {
  return {
    ...child,
    props: {
      ...child.props,
      children: renderGroup(child?.props?.children, clone),
    },
  };
}
